import * as THREE from 'three';

export function analyzeUVChannels() {
    if (!this.mesh) return;
    this.uvChannels = [];
    this.udimTiles = new Set();
    const container = document.getElementById('uvChannels');
    if (container) container.innerHTML = '';
    let channelCount = 0;

    this.mesh.traverse(child => {
        if (child.isMesh && child.geometry) {
            const g = child.geometry;
            ['uv', 'uv2', 'uv3', 'uv4'].forEach((attr, i) => {
                if (g.attributes[attr]) {
                    channelCount = Math.max(channelCount, i + 1);
                    if (i < 2) this.analyzeUDIMTiles(g.attributes[attr]);
                }
            });
        }
    });

    const tiles = Array.from(this.udimTiles).sort();
    if (!tiles.length) tiles.push(1001);
    tiles.forEach((tile, i) => this.createUVChannelControl(i, tile));
    this.setupBasicMaterials();
    this.refreshCharacterMaterials();
}

export function setupBasicMaterials() {
    const material = this.uvChannels.length > 0
        ? new THREE.MeshLambertMaterial({ map: this.uvChannels[0].texture, side: THREE.DoubleSide })
        : new THREE.MeshLambertMaterial({ map: this.createDefaultTexture(), side: THREE.DoubleSide });

    this.mesh.traverse(child => {
        if (child.isMesh) child.material = material.clone();
    });
    this.render();
}

export function createDefaultTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 512;
    const ctx = canvas.getContext('2d');
    const color = this.getSelectedBaseSkinColor ? this.getSelectedBaseSkinColor() : '#f4d1ae';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 512, 512);
    const texture = new THREE.CanvasTexture(canvas);
    texture.flipY = false;
    return texture;
}

export function analyzeUDIMTiles(uvAttribute) {
    const uvArray = uvAttribute.array;
    for (let i = 0; i < uvArray.length; i += 2)
        this.udimTiles.add(this.calculateUDIMTile(uvArray[i], uvArray[i + 1]));
}

export function calculateUDIMTile(u, v) {
    return 1001 + Math.floor(u) + Math.floor(v) * 10;
}

export function createUVChannelControl(index, udimTile = 1001) {
    const container = document.getElementById('uvChannels');
    const res = this.textureResolution;
    const preview = 256;

    const defaultUdimLabels = { 1001: 'Head', 1002: 'Upper Body', 1003: 'Lower Body', 1004: 'Ears' };
    const map = (this && this.udimNameMap) ? this.udimNameMap
              : (this && this.isDefaultMesh ? defaultUdimLabels : null);
    const headerName = (map && map[udimTile]) ? map[udimTile] : `UDIM ${udimTile}`;

    const div = document.createElement('div');
    div.className = 'uv-channel';
    div.innerHTML = `<h4>${headerName} (${res}x${res})</h4>
        <div style="display:flex;gap:5px;">
            <div style="flex:1;"><label style="font-size:10px;">Base Texture</label>
                <canvas id="baseCanvas${index}" width="${preview}" height="${preview}" style="width:100%;border:1px solid #555;background:rgba(64,64,64,0.5);"></canvas>
            </div>
            <div style="flex:1;"><label style="font-size:10px;">Decal Layer</label>
                <canvas id="uvCanvas${index}" width="${preview}" height="${preview}" style="width:100%;border:1px solid #555;background:rgba(0,0,0,0.1);"></canvas>
            </div>
        </div>
        <canvas id="compositeCanvas${index}" width="${res}" height="${res}" style="width:100%;border:1px solid #777;margin-top:5px;"></canvas>
        <button onclick="texturePainter.exportTextures(${index})">Export ${headerName} Decal Texture</button>`;
    if (container) container.appendChild(div);

    const getCtx = id => { const c = document.getElementById(id); c.width = c.height = res; return c.getContext('2d'); };
    const decalCtx = getCtx(`uvCanvas${index}`);
    const baseCtx = getCtx(`baseCanvas${index}`);
    const compositeCanvas = document.getElementById(`compositeCanvas${index}`);
    const compositeCtx = compositeCanvas.getContext('2d');

    decalCtx.clearRect(0, 0, res, res);
    baseCtx.fillStyle = this.getSelectedBaseSkinColor ? this.getSelectedBaseSkinColor() : '#f4d1ae';
    baseCtx.fillRect(0, 0, res, res);

    const decalRenderTarget = new THREE.WebGLRenderTarget(res, res, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        generateMipmaps: false
    });

    const texture = new THREE.CanvasTexture(compositeCanvas);
    Object.assign(texture, {
        generateMipmaps: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat, flipY: true, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping
    });

    const decalCanvasEl = document.getElementById(`uvCanvas${index}`);
    const baseCanvasEl = document.getElementById(`baseCanvas${index}`);

    this.uvChannels[index] = {
        decalCanvas: decalCanvasEl, decalContext: decalCtx,
        baseCanvas: baseCanvasEl, baseContext: baseCtx,
        compositeCanvas, compositeContext: compositeCtx, texture, udimTile, decalRenderTarget
    };

    if (decalCanvasEl) {
        decalCanvasEl.style.cursor = 'pointer';
        decalCanvasEl.title = 'Click to load a DECAL image and override this layer';
        const self = this;
        decalCanvasEl.addEventListener('click', () => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.style.display = 'none';
            document.body.appendChild(input);

            input.addEventListener('change', async (e) => {
                const file = e.target.files && e.target.files[0];
                document.body.removeChild(input);
                if (!file) return;

                const host = self || (typeof window !== 'undefined' ? window.texturePainter : null);
                if (!host) {
                    console.warn('Painter instance not found to load decal image');
                    return;
                }
                try {
                    if (typeof host.loadDecalImageIntoChannel === 'function') {
                        await host.loadDecalImageIntoChannel(index, file);
                    } else if (typeof loadDecalImageIntoChannel === 'function') {
                        await loadDecalImageIntoChannel.call(host, index, file);
                    } else {
                        console.warn('loadDecalImageIntoChannel not available');
                        return;
                    }
                    host.updateStatus?.(`Loaded decal image into UDIM ${host.uvChannels?.[index]?.udimTile || ''}`);
                } catch (err) {
                    console.error('Failed to load decal image:', err);
                    host.updateStatus?.(`Error loading decal image: ${err.message || err}`);
                }
            });

            input.click();
        });
    }

    this.compositeTexture(index);
    if (index === 0) this.refreshCharacterMaterials();
}

export function updateAllUDIMTextures() {
    if (this.projectionMode === 'brush') {
        this.uvChannels.forEach((channel, i) => {
            if (channel.texture.needsUpdate !== false) {
                this.compositeTexture(i);
            }
        });
    } else {
        this.uvChannels.forEach((channel, i) => this.compositeTexture(i));
        this.refreshCharacterMaterials();
    }
}

export function updateTextureResolution(resolution) {
    this.textureResolution = resolution;
    if (this.uvChannels.length > 0) {
        const recreate = confirm(`Change resolution to ${resolution}x${resolution}? This will recreate all existing texture channels and clear any painted content.`);
        if (recreate) {
            const currentTiles = this.uvChannels.map(channel => channel.udimTile);
            this.uvChannels = [];
            const container = document.getElementById('uvChannels');
            if (container) container.innerHTML = '';
            currentTiles.forEach((tile, index) => this.createUVChannelControl(index, tile));
            this.applyUDIMTextures();
            this.updateStatus(`All texture channels recreated with ${resolution}x${resolution} resolution.`);
        } else {
            this.updateStatus(`Texture resolution set to ${resolution}x${resolution}. Only new channels will use this resolution.`);
        }
    } else {
        this.updateStatus(`Texture resolution set to ${resolution}x${resolution}.`);
    }
}

export function openFilePickerForDecal(channelIndex) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) {
            document.body.removeChild(input);
            return;
        }
        try {
            await this.loadDecalImageIntoChannel(channelIndex, file);
            this.updateStatus?.(`Loaded decal image into UDIM ${this.uvChannels[channelIndex]?.udimTile || ''}`);
        } catch (err) {
            console.error('Failed to load decal image:', err);
            this.updateStatus?.(`Error loading decal image: ${err.message || err}`);
        } finally {
            document.body.removeChild(input);
        }
    });

    input.click();
}

export function loadDecalImageIntoChannel(channelIndex, fileOrUrl) {
    return new Promise((resolve, reject) => {
        const channel = this.uvChannels?.[channelIndex];
        if (!channel) {
            reject(new Error('Invalid channel index'));
            return;
        }
        const loadFromUrl = (url) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const { decalCanvas, decalContext } = channel;
                decalContext.clearRect(0, 0, decalCanvas.width, decalCanvas.height);

                const cw = decalCanvas.width;
                const ch = decalCanvas.height;
                const iw = img.width;
                const ih = img.height;
                const scale = Math.min(cw / iw, ch / ih);
                const drawW = Math.max(1, Math.floor(iw * scale));
                const drawH = Math.max(1, Math.floor(ih * scale));
                const dx = Math.floor((cw - drawW) / 2);
                const dy = Math.floor((ch - drawH) / 2);

                decalContext.imageSmoothingEnabled = true;
                decalContext.imageSmoothingQuality = 'high';
                decalContext.drawImage(img, 0, 0, iw, ih, dx, dy, drawW, drawH);

                this.compositeTexture?.(channelIndex);
                channel.texture.needsUpdate = true;

                this.refreshCharacterMaterials?.();
                resolve();
            };
            img.onerror = reject;
            img.src = url;
        };

        if (typeof fileOrUrl === 'string') {
            loadFromUrl(fileOrUrl);
        } else if (fileOrUrl instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => loadFromUrl(e.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(fileOrUrl);
        } else {
            reject(new Error('Unsupported decal source'));
        }
    });
}
